/**
 * React NodeView for `workItemEmbed` — Phase 4c inline atomic embed.
 *
 * Visual: an inline-atomic chip mirroring the date / status chip
 * shape (per UX Agent 3 §4) — kind-stripe + code chip + title + status
 * pill + assignee avatar. Renders three terminal states beyond the
 * happy path:
 *
 *   - Placeholder (empty attrs)   — "Pick a work item…" italic chip
 *   - Loading                      — pulse-skeleton chip
 *   - Restricted (BE 403)          — "[Restricted]" greyed chip
 *   - Deleted (BE 404)             — "[Deleted]" strikethrough chip
 *   - Data                         — full live chip
 *
 * Click behavior depends on whether the surface is editable:
 *
 *   - **Read-only** — the chip is a plain `<a target="_blank">` to the
 *     card's own FULL-PAGE route. Same convention as the `[[wikilink]]`
 *     chip (per the `[FE/Editor]` pitfall in the FE CLAUDE.md —
 *     `target="_blank"` sidesteps the unsaved-changes nav guard + the
 *     ProseMirror selection fight on atom click).
 *   - **Editable** — the click is intercepted and opens
 *     `<WorkItemChipMenu>` (Open card / Copy link / Convert to text). An
 *     author mid-edit is more likely to want to undo the chip than to
 *     navigate away, and auto-detected chips appear without being asked
 *     for, so there has to be a one-click way back to plain text. A
 *     Cmd/Ctrl/Shift-click still opens the card directly.
 *
 * In-place modal open is intentionally NOT implemented; the Phase 4 plan
 * calls it "the in-place is too messy" fallback. Link-out is the
 * canonical path.
 *
 * Realtime: `useCardEmbed` subscribes to `card.updated` / `card.deleted`
 * WS frames and invalidates its own cache. The chip re-renders without
 * the NodeView needing any explicit subscription.
 *
 * Locale re-render: the kind label + status pill text + assignee
 * placeholder pass through `t()` from `useTranslation('wiki')`; a
 * locale flip re-renders the NodeView through i18next's React adapter.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { type MouseEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DeliverableKind } from '@/api/types-bridge'
import { WorkItemChipBody } from '@/components/WorkItemChipBody'
import { buildCardDeepLinkPath } from '@/features/board/card-deep-link'
import { useCardEmbed } from '@/features/wiki/hooks/use-card-embed'

import { WorkItemChipMenu } from './WorkItemChipMenu'

export function WorkItemEmbedView({ node, editor, getPos }: ReactNodeViewProps<HTMLElement>) {
  const { t } = useTranslation('wiki')
  const projectId = (node.attrs.projectId as string | null) ?? null
  const cardId = (node.attrs.cardId as string | null) ?? null
  const shorthand = (node.attrs.shorthand as string | null) ?? null
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null)

  /**
   * Replace the chip with the plain reference it stands for. Dispatched as
   * a raw transaction rather than through `insertContentAt`, which
   * tiptap-markdown overrides to markdown-PARSE string content — harmless
   * for a bare `MOTE-30` today, but the wrong dependency to take on.
   *
   * The detection rule only fires on typed input, so this does not
   * immediately re-convert.
   */
  const convertToText = useCallback(() => {
    const pos = getPos()
    if (typeof pos !== 'number') return
    const text = shorthand ?? (node.attrs.cardId as string | null)
    if (!text) return
    editor.view.dispatch(
      editor.state.tr.replaceWith(pos, pos + node.nodeSize, editor.state.schema.text(text)),
    )
    editor.commands.focus()
  }, [editor, getPos, node, shorthand])

  /**
   * In an editable surface the chip opens a menu instead of navigating —
   * see `WorkItemChipMenu`. Read-only surfaces keep the plain link, which
   * is why this bails rather than always intercepting.
   */
  const onChipClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!editor.isEditable) return
      // Let a deliberate new-tab click through untouched.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      setMenuAnchor(event.currentTarget.getBoundingClientRect())
    },
    [editor],
  )
  // Lookup hint only. Chips authored before the attr existed (or pasted
  // without it) pass `undefined` and resolve via the BE's discriminator
  // — one extra statement, identical payload. The `kind` we RENDER
  // below always comes from the response, never from this attr.
  const kindHint = (node.attrs.kind as DeliverableKind | null) ?? undefined

  const state = useCardEmbed(cardId, kindHint)

  // Placeholder — attrs missing (the user inserted the chip but
  // hasn't picked a card yet, OR the picker was cancelled). Render a
  // subtle italic prompt the user can click to navigate; v1 expects
  // the picker auto-open on insert to fill the attrs immediately, so
  // this branch is the safety net.
  if (!projectId || !cardId) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="work-item-embed"
        data-state="placeholder"
        className="relative inline-block max-w-full align-baseline"
      >
        <span
          className="wiki-inline-chip italic text-fg-subtle"
          aria-label={t('workItem.placeholderAriaLabel', { defaultValue: 'Pick a work item' })}
        >
          {t('workItem.placeholder', { defaultValue: 'Pick a work item…' })}
        </span>
      </NodeViewWrapper>
    )
  }

  // Loading skeleton — render the chip outline with a pulse so the
  // line height doesn't jump when data lands. Width approximates the
  // resolved chip size; line-height stays in baseline alignment.
  if (state.isLoading) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="work-item-embed"
        data-card-id={cardId}
        data-state="loading"
        className="relative inline-block max-w-full align-baseline"
      >
        <span
          className="wiki-inline-chip animate-pulse bg-bg-muted text-fg-subtle"
          aria-label={t('workItem.loading', { defaultValue: 'Loading work item…' })}
        >
          {t('workItem.loading', { defaultValue: 'Loading work item…' })}
        </span>
      </NodeViewWrapper>
    )
  }

  if (state.isRestricted) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="work-item-embed"
        data-card-id={cardId}
        data-state="restricted"
        className="relative inline-block max-w-full align-baseline"
      >
        <span
          className="wiki-inline-chip cursor-not-allowed bg-bg-muted text-fg-subtle"
          title={t('workItem.restrictedTitle', { defaultValue: 'Restricted' })}
        >
          {t('workItem.restricted', { defaultValue: '[Restricted]' })}
        </span>
      </NodeViewWrapper>
    )
  }

  if (state.isDeleted) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="work-item-embed"
        data-card-id={cardId}
        data-state="deleted"
        className="relative inline-block max-w-full align-baseline"
      >
        <span
          className="wiki-inline-chip cursor-not-allowed bg-bg-muted text-fg-subtle line-through"
          title={t('workItem.deletedTitle', { defaultValue: 'Deleted' })}
        >
          {t('workItem.deleted', { defaultValue: '[Deleted]' })}
        </span>
      </NodeViewWrapper>
    )
  }

  // Happy path — full chip render.
  const card = state.data
  const codeText = card.code ?? card.id.slice(0, 8)
  const ariaLabel = t('workItem.ariaLabel', {
    code: codeText,
    defaultValue: 'Embedded work item {code}',
  })

  // The card's own full-page route, opened in a new tab via
  // `target="_blank"` — matches the wikilink convention so the
  // unsaved-changes nav guard doesn't fire mid-edit. Following a
  // reference out of a document should land on the referenced CARD, not
  // on the board it happens to live on with the card as a modal over it.
  //
  // The board deep link stays as the fallback for the one shape the
  // page routes can't express (a task or ticket with no shorthand) — a
  // chip is never a dead link.
  const href =
    buildCardDeepLinkPath({ kind: card.kind, id: card.id, shorthand: card.code }) ??
    `/board/${card.projectId}?selectedDeliverable=${card.id}`

  return (
    <NodeViewWrapper
      as="span"
      data-block-type="work-item-embed"
      data-card-id={cardId}
      data-project-id={card.projectId}
      data-state="ready"
      className="relative inline-block max-w-full align-baseline"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        title={card.title}
        onClick={onChipClick}
        className="wiki-inline-chip wiki-inline-chip--work-item no-underline"
      >
        {/* Shared with the sent-chat-message renderer — see
            `WorkItemChipBody`. Code + title joined by ":" is Jira's
            `MNT-951: Reducing Meta Queries` shape.

            No assignee avatar: an inline reference answers "which work
            item", and who currently holds it is card-detail information
            that changes independently of the sentence, so a face inside a
            run of prose drew the eye away from the reference it was meant
            to decorate. */}
        <WorkItemChipBody
          kind={card.kind}
          code={codeText}
          title={card.title}
          statusName={card.status?.name ?? null}
        />
      </a>
      {menuAnchor ? (
        <WorkItemChipMenu
          anchor={menuAnchor}
          href={href}
          onConvertToText={convertToText}
          onClose={() => setMenuAnchor(null)}
        />
      ) : null}
    </NodeViewWrapper>
  )
}

WorkItemEmbedView.displayName = 'WorkItemEmbedView'
